/**
 * Returns the search radius in miles based on the place type/s.
 * Different address granularities (street, city, state, etc.) require different search radius.
 *
 * Supports both Google Places types and Radar layers.
 *
 * @see https://developers.google.com/maps/documentation/places/web-service/supported_types
 * @see https://radar.com/documentation/api#autocomplete
 */
export function getSearchRadiusByPlaceType(
  types: readonly string[] | string[] | undefined,
): number {
  const hasNoTypes = !types || types.length === 0;

  // ─────────────────────────────────────────────────────────────────────────────

  if (hasNoTypes) {
    return RADIUSES.SMALL;
  }

  // Very specific locations: addresses, establishments, premises

  if (
    types.includes('address') ||
    types.includes('place') ||
    types.includes('fine') ||
    types.includes('establishment') ||
    types.includes('street_address') ||
    types.includes('premise') ||
    types.includes('subpremise')
  ) {
    return RADIUSES.SMALLEST;
  }

  // City/neighborhood level: postal codes, neighborhoods

  if (
    types.includes('postalCode') ||
    types.includes('coarse') ||
    types.includes('neighborhood') ||
    types.includes('sublocality') ||
    types.includes('sublocality_level_1')
  ) {
    return RADIUSES.SMALL;
  }

  // County/city level: counties, administrative areas, postal codes, localities

  if (
    types.includes('county') ||
    types.includes('locality') ||
    types.includes('postal_code') ||
    types.includes('administrative_area_level_2')
  ) {
    return RADIUSES.MEDIUM;
  }

  // State/country level: states, countries, administrative areas

  if (
    types.includes('state') ||
    types.includes('country') ||
    types.includes('administrative_area_level_1')
  ) {
    return RADIUSES.LARGE;
  }

  // ─────────────────────────────────────────────────────────────────────────────

  return RADIUSES.SMALL;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const RADIUSES = {
  SMALLEST: 2,
  SMALL: 15,
  MEDIUM: 40,
  LARGE: 200,
} as const;
